import { languageConfig } from '../config/languageConfig';

// === MASTERY ===
export function masteryLevel(progress, es) {
  const p = progress?.[es];
  if (!p || p.c === 0) return 'new';
  if (p.s < 3) return 'learning';
  if (p.s >= 6 && (p.drillStats?.produce?.c ?? 0) >= 3) return 'mastered';
  return 'strong';
}

export function getStats(words, progress) {
  const s = { new: 0, learning: 0, strong: 0, mastered: 0 };
  words.forEach(w => { s[masteryLevel(progress, w.es)]++; });
  return s;
}

export function dotColor(lvl) {
  return { new: '#D4D4D4', learning: '#FBBF24', strong: '#34D399', mastered: '#16A34A' }[lvl] || '#D4D4D4';
}

// === SPACED REPETITION ===
export function spacedRepetitionSort(words, progress) {
  const getWeight = (w) => {
    const p = progress?.[w.es];
    if (!p || p.c === 0) return 3;
    if (p.s >= 6) return 0.5;
    if (p.s >= 3) return 1.5;
    if (p.w > p.c * 0.5) return 5;
    return 2.5;
  };
  return [...words]
    .map(w => ({ w, score: getWeight(w) * Math.random() }))
    .sort((a, b) => b.score - a.score)
    .map(x => x.w);
}

export function buildNoRepeatQueue(words, progress, total) {
  if (words.length === 0 || total === 0) return [];
  const queue = [];
  const seen = new Set();
  while (queue.length < total) {
    if (seen.size >= words.length) seen.clear();
    const pool = words.filter(w => !seen.has(w.es));
    for (const w of spacedRepetitionSort(pool, progress)) {
      if (queue.length >= total) break;
      queue.push(w);
      seen.add(w.es);
    }
  }
  return queue;
}

// === RANDOM UTILS ===
export function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

export function pick(arr, n, exclude = []) {
  return shuffle(arr.filter(x => !exclude.includes(x))).slice(0, n);
}

// === ACCENT STRIPPING ===
// Accents are never penalised — strip before comparison in all drill modes
export function stripAccents(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// === LEVENSHTEIN ===
export function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => {
      if (i === 0) return j;
      if (j === 0) return i;
      return 0;
    })
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

// Split stored translation into alternate answers (e.g. "store / shop" → ["store", "shop"])
export function splitTranslationOptions(str) {
  if (!str) return [''];
  return str.split(' / ').map(s => s.trim()).filter(Boolean);
}

function allowedTyposForLength(len, strictMode) {
  if (strictMode) return 0;
  if (len >= 6) return 2;
  if (len >= 4) return 1;
  return 0;
}

// Grade a typed drill answer against one stored translation (may contain " / " alternates).
// Exact match on any alternate = correct (not "Almost"). Almost only for spelling near-misses.
export function gradeTypedAnswer(userAnswer, storedAnswer, strictMode = false) {
  const ans = stripAccents(userAnswer.trim().toLowerCase());
  const options = splitTranslationOptions(storedAnswer);
  const displayTarget = storedAnswer;

  for (const raw of options) {
    const tgt = stripAccents(raw.trim().toLowerCase());
    if (ans === tgt) {
      return { ok: true, closeEnough: false, matchedTarget: raw.trim(), displayTarget };
    }
  }

  if (!strictMode) {
    let bestNearMiss = null;
    for (const raw of options) {
      const tgt = stripAccents(raw.trim().toLowerCase());
      const dist = levenshtein(ans, tgt);
      const allowed = allowedTyposForLength(tgt.length, strictMode);
      if (dist > 0 && dist <= allowed && (!bestNearMiss || dist < bestNearMiss.dist)) {
        bestNearMiss = { dist, matchedTarget: raw.trim() };
      }
    }
    if (bestNearMiss) {
      return {
        ok: true,
        closeEnough: true,
        matchedTarget: bestNearMiss.matchedTarget,
        displayTarget: bestNearMiss.matchedTarget,
      };
    }
  }

  return { ok: false, closeEnough: false, matchedTarget: null, displayTarget };
}

// === SPEECH ===
let targetVoice = null;

export function initVoice() {
  const load = () => {
    const vs = window.speechSynthesis?.getVoices() || [];
    targetVoice = vs.find(v => v.lang === languageConfig.sourceLanguageCode) || vs.find(v => v.lang.startsWith(languageConfig.sourceLanguage)) || null;
  };
  if (window.speechSynthesis?.getVoices().length) load();
  else if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = load;
}

export function speak(text, lang = languageConfig.sourceLanguage, rate = 0.85) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  if (lang === languageConfig.sourceLanguage) {
    u.lang = targetVoice ? targetVoice.lang : languageConfig.sourceLanguageCode;
    if (targetVoice) u.voice = targetVoice;
    u.rate = rate;
  } else {
    u.lang = languageConfig.targetLanguageCode;
    u.rate = rate;
  }
  window.speechSynthesis.speak(u);
}

export const sanitiseForTTS = (text) => {
  if (!text) return text;
  return text
    .replace(/\s*\/\s*/g, ' , or , ')
    .replace(/\(f\)/gi, ', feminine')
    .replace(/\(m\)/gi, ', masculine');
};

// === TAP SOUND ===
export function playTap() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = 1200;
    oscillator.type = 'sine';
    gain.gain.setValueAtTime(1.0, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.04);
  } catch (e) {}
}

// === CONFETTI SOUND ===
let confettiBuffer = null;
let audioCtx = null;
let activeConfettiSource = null;

function getAudioContext() {
  try {
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtx;
  } catch (e) { return null; }
}

export async function initAudio() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    const res = await fetch('/audio/fanfare.mp3');
    if (res.ok) confettiBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
  } catch (e) {}
}

export function playConfetti() {
  try {
    const ctx = getAudioContext();
    if (!ctx || !confettiBuffer) return;
    if (ctx.state === 'suspended') ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = confettiBuffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start();
    activeConfettiSource = source;
    setTimeout(() => {
      try { source.stop(); activeConfettiSource = null; } catch (e) {}
    }, 6600);
  } catch (e) {}
}

export function stopConfetti() {
  try {
    if (activeConfettiSource) {
      activeConfettiSource.stop();
      activeConfettiSource = null;
    }
  } catch (e) {}
}